import { lazy, Suspense, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Loader2 } from "lucide-react";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { UserSettingsProvider } from "@/contexts/UserSettingsContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AutoSyncProvider } from "@/components/AutoSyncProvider";

// Lazy-load toast components (not needed for initial render)
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// PWAUpdatePrompt needs LanguageProvider, so load eagerly
import { PWAUpdatePrompt } from "@/components/PWAUpdatePrompt";
import { ScreenshotSessionProvider } from "@/components/ScreenshotSession";

// Only truly critical path - Landing page (no auth required)
import Landing from "./pages/Landing";

// Lazy-loaded: Authentication (only when user clicks login)
const Auth = lazy(() => import("./pages/Auth"));

// Lazy-loaded: Legal and public pages (rarely accessed)
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const Contact = lazy(() => import("./pages/Contact"));

const OnboardingTest = lazy(() => import("./pages/OnboardingTest"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy-loaded: Protected layout and components (only after auth)
const ProtectedLayout = lazy(() => import("@/components/ProtectedLayout").then(m => ({ default: m.ProtectedLayout })));
const WelcomeOverlay = lazy(() => import("@/components/WelcomeOverlay").then(m => ({ default: m.WelcomeOverlay })));
const ResumeOverlay = lazy(() => import("@/components/ResumeOverlay").then(m => ({ default: m.ResumeOverlay })));
const Layout = lazy(() => import("@/components/Layout").then(m => ({ default: m.Layout })));

// Lazy-loaded: Dashboard pages (after login)
const Dashboard = lazy(() => import("./pages/Index"));
const Lists = lazy(() => import("./pages/Lists"));
const ListDetail = lazy(() => import("./pages/ListDetail"));
const Learn = lazy(() => import("./pages/Learn"));
const Profile = lazy(() => import("./pages/Profile"));

// Lazy-loaded: Exercise sessions (only when user starts exercise)
const FlashcardSession = lazy(() => import("./pages/FlashcardSession"));
const MultipleChoiceSession = lazy(() => import("./pages/MultipleChoiceSession"));
const TypingSession = lazy(() => import("./pages/TypingSession"));
const WordPairsSession = lazy(() => import("./pages/WordPairsSession"));
const DialogueSession = lazy(() => import("./pages/DialogueSession"));
const ListeningSession = lazy(() => import("./pages/ListeningSession"));
// Arabic-specific sessions removed for GermanPath
const ZahlenListenSession = lazy(() => import("./pages/ZahlenListenSession"));
const ZahlenIntroSession = lazy(() => import("./pages/ZahlenIntroSession"));
const UhrzeitListenSession = lazy(() => import("./pages/UhrzeitListenSession"));
const UhrzeitIntroSession = lazy(() => import("./pages/UhrzeitIntroSession"));
const ErsteWoerterSession = lazy(() => import("./pages/ErsteWoerterSession"));
const FarbenSession = lazy(() => import("./pages/FarbenSession"));
const GeldSession = lazy(() => import("./pages/GeldSession"));
// Lazy-loaded: Admin pages (rare access)
const AdminFlags = lazy(() => import("./pages/AdminFlags"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminChangelog = lazy(() => import("./pages/AdminChangelog"));
const AdminDuplicates = lazy(() => import("./pages/AdminDuplicates"));
const AdminDialogues = lazy(() => import("./pages/AdminDialogues"));
const AdminArticleCheck = lazy(() => import("./pages/AdminArticleCheck"));
const AdminAudioGenerator = lazy(() => import("./pages/AdminAudioGenerator"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminCategories = lazy(() => import("./pages/AdminCategories"));
const AdminSupport = lazy(() => import("./pages/AdminSupport"));
const AdminSorting = lazy(() => import("./pages/AdminSorting"));
const AdminExerciseItems = lazy(() => import("./pages/AdminExerciseItems"));
// TestRunner removed for GermanPath
const AdminOnboardingSlides = lazy(() => import("./pages/AdminOnboardingSlides"));
const AdminKnowledge = lazy(() => import("./pages/AdminKnowledge"));
const AdminVideoAssets = lazy(() => import("./pages/AdminVideoAssets"));
const AdminLessonExercises = lazy(() => import("./pages/AdminLessonExercises"));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// Export queryClient so it can be used in AuthContext for cache clearing
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});
// Guard for admin/teacher-only routes
function AdminRouteGuard({ children }: { children: ReactNode }) {
  const { isAdmin, isTeacher, roleLoading } = useAuth();
  if (roleLoading) return null;
  if (!isAdmin && !isTeacher) return <Navigate to="/learn" replace />;
  return <>{children}</>;
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
        <TooltipProvider>
          <LanguageProvider>
            <PWAUpdatePrompt />
            <UserSettingsProvider>
              <AutoSyncProvider>
              <AuthProvider>
                <ScreenshotSessionProvider>
                <Suspense fallback={null}>
                  <Toaster />
                  <Sonner />
                </Suspense>
                <BrowserRouter>
                  <Suspense fallback={null}>
                    <WelcomeOverlay />
                    <ResumeOverlay />
                  </Suspense>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      {/* Public routes */}
                      <Route path="/" element={<Landing />} />
                      <Route path="/auth" element={<Auth />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/impressum" element={<Impressum />} />
                      <Route path="/datenschutz" element={<Datenschutz />} />
                      
                      <Route path="/test/onboarding" element={<OnboardingTest />} />

                      {/* Protected routes with shared layout */}
                      <Route element={<ProtectedLayout />}>
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/lists" element={<AdminRouteGuard><Lists /></AdminRouteGuard>} />
                        <Route path="/lists/:listId" element={<AdminRouteGuard><ListDetail /></AdminRouteGuard>} />
                        <Route path="/learn" element={<Learn />} />
                        {/* Session routes with listId and optional size parameter */}
                        <Route path="/flashcard/:listId/:size?" element={<FlashcardSession />} />
                        <Route path="/multiple-choice/:listId/:size?" element={<MultipleChoiceSession />} />
                        <Route path="/typing/:listId/:size?" element={<TypingSession />} />
                        <Route path="/word-pairs/:listId" element={<WordPairsSession />} />
                        <Route path="/dialogue/:listId" element={<DialogueSession />} />
                        <Route path="/listening/:listId/:size?" element={<ListeningSession />} />
                        {/* Topic-specific exercises */}
                        <Route path="/learn/zahlen-listen" element={<ZahlenListenSession />} />
                        <Route path="/learn/zahlen-intro" element={<ZahlenIntroSession />} />
                        <Route path="/learn/uhrzeit-listen" element={<UhrzeitListenSession />} />
                        <Route path="/learn/uhrzeit-intro" element={<UhrzeitIntroSession />} />
                        <Route path="/learn/erste-woerter" element={<ErsteWoerterSession />} />
                        <Route path="/learn/farben" element={<FarbenSession />} />
                        <Route path="/learn/geld" element={<GeldSession />} />
                        <Route path="/profile" element={<Profile />} />
                        <Route path="/admin/flags" element={<AdminFlags />} />
                        <Route path="/admin/users" element={<AdminUsers />} />
                        <Route path="/admin/changelog" element={<AdminChangelog />} />
                        <Route path="/admin/duplicates" element={<AdminDuplicates />} />
                        {/* TestRunner removed for GermanPath */}
                        <Route path="/admin/dialogues" element={<AdminDialogues />} />
                        <Route path="/admin/article-check" element={<AdminArticleCheck />} />
                        <Route path="/admin/audio" element={<AdminAudioGenerator />} />
                        <Route path="/admin/analytics" element={<AdminAnalytics />} />
                        <Route path="/admin/categories" element={<AdminCategories />} />
                        <Route path="/admin/support" element={<AdminSupport />} />
                        <Route path="/admin/sorting" element={<AdminSorting />} />
                        <Route path="/admin/exercise-items" element={<AdminExerciseItems />} />
                        <Route path="/admin/onboarding" element={<AdminOnboardingSlides />} />
                        <Route path="/admin/knowledge" element={<AdminKnowledge />} />
                        <Route path="/admin/video-assets" element={<AdminVideoAssets />} />
                        <Route path="/admin/lesson-exercises" element={<AdminLessonExercises />} />
                        
                      </Route>

                      {/* Not found */}
                      <Route
                        path="*"
                        element={
                          <Layout>
                            <NotFound />
                          </Layout>
                        }
                      />
                    </Routes>
                  </Suspense>
                </BrowserRouter>
                </ScreenshotSessionProvider>
              </AuthProvider>
              </AutoSyncProvider>
            </UserSettingsProvider>
          </LanguageProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
